/**
 * Integrations management section for DP Operator Console.
 * Embedded as a tab within ConsolePage.
 * Per INTEGRATIONS_ARCHITECTURE.md §6.
 */
import { useState, useEffect } from 'react';
import { apiClient } from '../../lib/apiClient';
import IntegrationListTable from './IntegrationListTable';
import IntegrationFormModal from './IntegrationFormModal';
import IntegrationAuditLog from './IntegrationAuditLog';

export default function IntegrationsSection({ tenantId }) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [auditLogId, setAuditLogId] = useState(null);

  useEffect(() => { loadIntegrations(); }, []);

  async function loadIntegrations() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/dp/integrations');
      setIntegrations(res.data || res || []);
    } catch (err) {
      setError(err.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }

  async function handleTestConnection(integrationId) {
    try {
      const res = await apiClient.post(`/api/dp/integrations/${integrationId}/test`, {});
      const result = res.data || res;
      if (result.success) {
        alert(`Connection OK (${result.latency_ms}ms)`);
      } else {
        alert(`Connection failed: ${result.error_message}`);
      }
      await loadIntegrations();
    } catch (err) {
      alert(`Test failed: ${err.message}`);
    }
  }

  async function handleDisable(integrationId) {
    if (!confirm('Integration deaktivieren? Service wird für betroffene Tenants gestoppt.')) return;
    try {
      await apiClient.delete(`/api/dp/integrations/${integrationId}`);
      await loadIntegrations();
    } catch (err) {
      alert(`Deaktivierung fehlgeschlagen: ${err.message}`);
    }
  }

  async function handleSave(formData) {
    if (editingIntegration) {
      await apiClient.patch(`/api/dp/integrations/${editingIntegration.id}`, formData);
    } else {
      await apiClient.post('/api/dp/integrations', formData);
    }
    setModalOpen(false);
    setEditingIntegration(null);
    await loadIntegrations();
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Tenant Integrations (BYOK)</h2>
        <button
          className="bg-[var(--aif-color-primary)] text-white px-3 py-1.5 rounded text-sm"
          onClick={() => { setEditingIntegration(null); setModalOpen(true); }}
        >
          + Integration hinzufuegen
        </button>
      </div>

      {loading && <p className="text-sm text-[var(--aif-text-muted)]">Laden...</p>}
      {error && <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 p-3 rounded text-sm mb-3">{error}</div>}

      {!loading && integrations.length === 0 && (
        <div className="text-[var(--aif-text-muted)] p-8 text-center border-2 border-dashed border-[var(--aif-border-default)] rounded-lg">
          <p className="text-sm">Noch keine Integrationen konfiguriert.</p>
          <p className="text-xs mt-1">Klicken Sie auf "Integration hinzufuegen", um zu starten.</p>
        </div>
      )}

      {!loading && integrations.length > 0 && (
        <IntegrationListTable
          integrations={integrations}
          onEdit={(intg) => { setEditingIntegration(intg); setModalOpen(true); }}
          onTest={handleTestConnection}
          onDisable={handleDisable}
        />
      )}

      {modalOpen && (
        <IntegrationFormModal
          integration={editingIntegration}
          tenantId={tenantId}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditingIntegration(null); }}
        />
      )}

      {auditLogId && (
        <IntegrationAuditLog
          integrationId={auditLogId}
          onClose={() => setAuditLogId(null)}
        />
      )}
    </div>
  );
}
